import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/Dialog';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Input,
} from '@/shared/components/ui';
import {
  useMarketplaceSignals,
  useImportSignal,
  useRemoveImportedSignal,
  useActivateSignal,
  useDeactivateSignal,
} from '@/shared/hooks/useSignals';
import { useEmployees } from '@/shared/hooks/useEmployees';
import { useEmployeeContext } from '@/shared/contexts/EmployeeContext';
import { SignalInstalledStatus } from '@momentum/types';
import type { Signal, MarketplaceSignal } from '@momentum/types';
import { MarketplaceCard } from './MarketplaceCard';
import { SignalDetailModal } from '../components/SignalDetailModal';

interface MarketplaceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const isUserImported = (signal: Signal, userId: string): boolean =>
  (signal.installed?.users ?? []).some(
    (u) => u.userId === userId && u.status === SignalInstalledStatus.ACTIVE
  );

export const MarketplaceModal = ({
  open,
  onOpenChange,
}: MarketplaceModalProps) => {
  const { t } = useTranslation('knowledge');
  const currentUser = useEmployeeContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSignalId, setSelectedSignalId] = useState<string | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const { data: allSignals = [] } = useMarketplaceSignals({
    enabled: open,
  });
  const { data: employees = [] } = useEmployees({ isActive: true }, open);

  const importMutation = useImportSignal();
  const removeMutation = useRemoveImportedSignal();
  const activateMutation = useActivateSignal();
  const deactivateMutation = useDeactivateSignal();

  const employeeMap = useMemo(
    () =>
      new Map(
        employees.map((emp) => [
          emp._id,
          {
            fullName: `${emp.personalInfo.firstName} ${emp.personalInfo.lastName}`,
            imageUrl: emp.personalInfo.imageUrl,
            role: emp.role,
          },
        ])
      ),
    [employees]
  );

  const sortedSignals = useMemo(() => {
    if (!searchQuery.trim()) return allSignals;

    const query = searchQuery.toLowerCase();
    return allSignals.filter((signal) =>
      (signal.title ?? '').toLowerCase().includes(query)
    );
  }, [allSignals, searchQuery]);

  const importedSignals = useMemo(
    () =>
      sortedSignals.filter((signal) => isUserImported(signal, currentUser._id)),
    [sortedSignals, currentUser._id]
  );

  const selectedSignal =
    allSignals.find((l) => l._id === selectedSignalId) ?? null;

  const handleCardClick = (signal: Signal) => {
    setSelectedSignalId(signal._id);
    setIsDetailOpen(true);
  };

  const handleImport = (signalId: string) => {
    importMutation.mutate(signalId, {
      onSuccess: () => toast.success(t('marketplace.importSuccess')),
      onError: () => toast.error(t('marketplace.importError')),
    });
  };

  const handleRemove = (signalId: string) => {
    removeMutation.mutate(signalId, {
      onSuccess: () => toast.success(t('marketplace.removeSuccess')),
      onError: () => toast.error(t('marketplace.removeError')),
    });
  };

  const renderGrid = (signals: MarketplaceSignal[]) => {
    if (signals.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <p className="text-muted-foreground">
            {t('marketplace.noSignalsFound')}
          </p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
        {signals.map((signal) => {
          const globalIndex = allSignals.indexOf(signal);
          return (
            <MarketplaceCard
              key={signal._id}
              title={signal.title}
              rank={globalIndex}
              userCount={signal.userCount}
              isImported={isUserImported(signal, currentUser._id)}
              owner={employeeMap.get(signal.createdBy)}
              onCardClick={() => handleCardClick(signal)}
              onImport={() => handleImport(signal._id)}
              onRemove={() => handleRemove(signal._id)}
              isImporting={
                importMutation.isPending &&
                importMutation.variables === signal._id
              }
              isRemoving={
                removeMutation.isPending &&
                removeMutation.variables === signal._id
              }
            />
          );
        })}
      </div>
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent size="full" className="flex h-full flex-col gap-0 p-0">
          {/* White header area */}
          <DialogHeader className="px-6 pt-6 pb-4">
            <DialogTitle className="text-xl">
              {t('marketplace.title')}
            </DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="browse" className="flex min-h-0 flex-1 flex-col">
            {/* Tabs in white area */}
            <div className="px-6">
              <TabsList className="border-b border-[#f0ece6] bg-transparent rounded-none p-0 w-full justify-start">
                <TabsTrigger
                  value="browse"
                  className="rounded-none border-b-2 border-transparent text-[#a39a91] font-normal py-2 px-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-[#5b4a9e] data-[state=active]:font-bold data-[state=active]:border-[#7c6bc4]"
                >
                  {t('marketplace.browse')}
                </TabsTrigger>
                <TabsTrigger
                  value="subscriptions"
                  className="rounded-none border-b-2 border-transparent text-[#a39a91] font-normal py-2 px-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-[#5b4a9e] data-[state=active]:font-bold data-[state=active]:border-[#7c6bc4]"
                >
                  {t('marketplace.myImports')}
                  {importedSignals.length > 0 && (
                    <span className="ms-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[#5b4a9e]/10 px-1.5 text-xs font-medium text-[#5b4a9e]">
                      {importedSignals.length}
                    </span>
                  )}
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Gray content area */}
            <div className="flex min-h-0 flex-1 flex-col bg-[#f5f6f8]">
              {/* Search bar */}
              <div className="flex items-center gap-3 px-6 py-3">
                <div className="relative flex-1">
                  <Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder={t('marketplace.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="bg-white ps-9"
                  />
                </div>
                <span className="shrink-0 text-sm text-muted-foreground">
                  {t('marketplace.signalCount')(sortedSignals.length)}
                </span>
              </div>

              {/* Scrollable grid */}
              <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-2 pb-6">
                <TabsContent value="browse" className="mt-0">
                  {renderGrid(sortedSignals)}
                </TabsContent>

                <TabsContent value="subscriptions" className="mt-0">
                  {importedSignals.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <p className="text-muted-foreground">
                        {t('marketplace.noImports')}
                      </p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {t('marketplace.noImportsDescription')}
                      </p>
                    </div>
                  ) : (
                    renderGrid(importedSignals)
                  )}
                </TabsContent>
              </div>
            </div>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Signal detail modal */}
      <SignalDetailModal
        signal={selectedSignal}
        currentUserId={currentUser._id}
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        onDelete={() => setIsDetailOpen(false)}
        employeeMap={employeeMap}
        onToggleActive={async (signal, isActive) => {
          try {
            if (isActive) {
              await deactivateMutation.mutateAsync(signal._id);
            } else {
              await activateMutation.mutateAsync({
                id: signal._id,
              });
            }
          } catch {
            toast.error(
              isActive ? t('deactivateSignalError') : t('activateSignalError')
            );
          }
        }}
        isTogglingActive={
          activateMutation.isPending || deactivateMutation.isPending
        }
        onRemove={(signal) => {
          removeMutation.mutate(signal._id, {
            onSuccess: () => {
              setIsDetailOpen(false);
            },
          });
        }}
        isManager={false}
        directReportsCount={0}
        fullHierarchyCount={0}
      />
    </>
  );
};
